import { inject } from '@angular/core';
import { CanActivateFn, CanDeactivateFn, Router, UrlTree } from '@angular/router';
import { AlertController, AlertOptions } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { AudioCallService } from '@services/audio-call.service';
import { ConnectAudioService } from '@services/connect-audio.service';
import { ActiveAudioCallUiService } from '@call/services/active-audio-call-ui.service';
import { StateOverlayService } from '@state-routing';
import { CallSessionService } from '@call/services/call-session.service';

export const audioCallCanActivate: CanActivateFn = (): boolean | UrlTree => {
  const audioCallService: AudioCallService = inject(AudioCallService);

  if (audioCallService.hasActiveAudioCall()) {
    return true;
  }

  console.warn('No active audio call. Redirecting to calls');
  return inject(Router).createUrlTree(['/calls']);
};

export const audioCallCanDeactivate: CanDeactivateFn<unknown> = async (): Promise<boolean> => {
  const connectAudioService: ConnectAudioService = inject(ConnectAudioService);
  const activeAudioCallUiService: ActiveAudioCallUiService = inject(ActiveAudioCallUiService);
  const translate: TranslateService = inject(TranslateService);
  const alertController: AlertController = inject(AlertController);
  const stateOverlay: StateOverlayService = inject(StateOverlayService);
  const callSessionService = inject(CallSessionService);
  const identity = callSessionService.mediaSessionIdentity;

  if (callSessionService.canLeaveCall) return true;
  if (activeAudioCallUiService.isMinimized) return true;

  const opts: AlertOptions = {
    header: translate.instant('call.audioCall.alerts.stopCall.header'),
    message: translate.instant('call.audioCall.alerts.stopCall.message'),
    backdropDismiss: false,
    buttons: [
      {
        text: translate.instant('call.audioCall.alerts.stopCall.buttons.cancel'),
        role: 'cancel',
      },
      {
        text: translate.instant('call.audioCall.alerts.stopCall.buttons.confirm'),
        role: 'confirm',
        handler: (): void => {
          if (callSessionService.mediaSessionIdentity !== identity) return;
          connectAudioService.stopStreamSubject.next(true);
        }
      }
    ]
  };
  const alert: HTMLIonAlertElement = await alertController.create(opts);
  if (callSessionService.mediaSessionIdentity !== identity) {
    alert.remove();
    return false;
  }
  await stateOverlay.presentInCurrentHistoryEntry(alert);
  const result = await alert.onDidDismiss();
  await stateOverlay.waitUntilSettled(alert);
  return result.role === 'confirm' && (callSessionService.mediaSessionIdentity === identity
    || callSessionService.mediaSessionIdentity === null);
};
